import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { User, Mail, Phone, FileText } from 'lucide-react';

export const PortalTooltip = ({ client = {}, children }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [coords, setCoords] = useState({ left: 0, top: 0 });
    const [mounted, setMounted] = useState(false);
    const triggerRef = useRef(null);

    useEffect(() => {
        setMounted(true);
    }, []);

    const handleMouseEnter = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setCoords({
                left: rect.right + 10,
                top: rect.top + (rect.height / 2)
            });
            setIsVisible(true);
        }
    };

    const hasContactInfo = client.pc_fname || client.pc_email || (client.pc_tel && client.pc_tel !== '00000000000');
    const hasNotes = client.notes && client.notes.trim().length > 0;

    if (!hasContactInfo && !hasNotes) return <div className="inline-block">{children}</div>;

    const tooltipContent = (
        <div 
          className="fixed z-[9999] bg-slate-800 text-white p-3 rounded-lg shadow-xl border border-slate-700 text-xs w-64 animate-in fade-in zoom-in-95 duration-100 pointer-events-auto"
          style={{ left: `${coords.left}px`, top: `${coords.top}px`, transform: 'translateY(-50%)' }}
        >
          <div className="font-bold text-slate-100 border-b border-slate-600 pb-1 mb-2">Primary Contact</div>
          
          {client.pc_fname && (
              <div className="mb-1.5 flex items-start gap-2">
                  <User className="w-3.5 h-3.5 mt-0.5 text-slate-400 flex-shrink-0" />
                  <span className="font-semibold text-slate-200">{client.pc_fname}</span>
              </div>
          )}
          
          {client.pc_email && (
              <div className="mb-1.5 flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                  <span className="text-blue-300 break-all">{client.pc_email}</span>
              </div>
          )}
          
          {client.pc_tel && (
              <div className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                  <span className="text-slate-300">{client.pc_tel}</span>
              </div>
          )}

          {hasNotes && (
              <div className="flex items-start gap-2 mt-2 pt-2 border-t border-slate-600">
                  <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300 italic">{client.notes}</span>
              </div>
          )}
        </div>
    );

    return (
        <>
            <div ref={triggerRef} onMouseEnter={handleMouseEnter} onMouseLeave={() => setIsVisible(false)} className="inline-block">
                {children}
            </div>
            {mounted && isVisible && createPortal(tooltipContent, document.body)}
        </>
    );
};

export const NotesTooltip = ({ notes = '', children }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [coords, setCoords] = useState({ left: 0, top: 0 });
    const [mounted, setMounted] = useState(false);
    const triggerRef = useRef(null);

    useEffect(() => {
        setMounted(true);
    }, []);

    const handleMouseEnter = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setCoords({
                left: rect.right + 10,
                top: rect.top + (rect.height / 2)
            });
            setIsVisible(true);
        }
    };

    if (!notes) return children;

    const tooltipContent = (
        <div 
          className="fixed z-[9999] bg-slate-800 text-white p-3 rounded-lg shadow-xl border border-slate-700 text-xs w-64 animate-in fade-in zoom-in-95 duration-100 pointer-events-auto"
          style={{ left: `${coords.left}px`, top: `${coords.top}px`, transform: 'translateY(-50%)' }}
        >
          <div className="font-bold text-amber-400 border-b border-slate-600 pb-1 mb-2 flex items-center gap-2">
              <FileText className="w-3.5 h-3.5" />
              Notes
          </div>
          <div className="text-slate-300 italic break-words">{notes}</div>
        </div>
    );

    return (
        <>
            <div ref={triggerRef} onMouseEnter={handleMouseEnter} onMouseLeave={() => setIsVisible(false)} className="inline-block cursor-help">
                {children}
            </div>
            {mounted && isVisible && createPortal(tooltipContent, document.body)}
        </>
    );
};